/**
 * Oferty sprzetowe Marketplace (migracja 391) — czesc czysta.
 *
 * DLACZEGO OSOBNY PLIK: `ModulesPage.tsx` ma blisko 1800 linii, a te trzy
 * funkcje niosa reguly, ktore MUSZA byc przypilnowane testem — w szczegolnosci
 * to, ze dane metrologiczne pokazujemy Z ZLACZENIA, a nie z kopii w ofercie.
 * Wewnatrz komponentu daloby sie je sprawdzic dopiero przez wyrenderowanie
 * strony, czyli w praktyce nigdy.
 */

import { bruttoWGroszach } from '@insolitum/types';

/** Model z `fabrica_instrument_models`, doklejony do oferty przez zlaczenie. */
export interface ModelPrzyrzaduOferty {
  code: string;
  size_label?: string | null;
  mounting?: string | null;
  resolution?: number | string | null;
  range_max?: number | string | null;
  measures_uom_code?: string | null;
}

/** Wiersz `marketplace_devices` z doklejonym modelem. */
export interface UrzadzenieOferty {
  id: string;
  slug: string;
  name: string;
  category: string;
  short_description?: string | null;
  long_description?: string | null;
  photos?: unknown;
  price_kind: 'purchase' | 'lease_monthly' | 'on_request';
  price_net?: number | string | null;
  currency?: string | null;
  /** Stawka VAT W PROCENTACH (migracja 487). Brak = nie liczymy brutto. */
  vat_rate?: number | string | null;
  lead_time_days?: number | null;
  /** Nasz identyfikator katalogowy, wspolny ze sklepem realizujacym wysylke. */
  sku?: string | null;
  /**
   * Czy pozycje da sie KUPIC karta (migracja 659). Flage zapala synchronizacja
   * ze Stripe i tylko wtedy, gdy jest cena w Stripe oraz — przy sprzecie
   * odsprzedawanym — znany koszt zakupu.
   *
   * Opcjonalne, bo wiersz sprzed 659 jej nie niesie: brak znaczy „nie wiadomo",
   * czyli tyle samo, co „jeszcze nie". Zaden z tych stanow nie jest zgoda na
   * pobranie pieniedzy.
   */
  stripe_price_active?: boolean | null;
  fabrica_instrument_models?: ModelPrzyrzaduOferty | null;
}

/**
 * Trzy polki Marketplace (487).
 *
 * `software` to organy z wlasnym ekranem, `plugin` to wtyczki integracyjne
 * (`modules.module_kind = 'integration'` — cudzy system w naszym procesie,
 * bez wlasnego ekranu), `device` to `marketplace_devices`.
 *
 * To NIE jest kategoria dziedzinowa. Waga jest jednoczesnie „Wagi i wazenie"
 * i urzadzeniem; te dwie osie sa prostopadle i musza zostac osobno.
 */
export type PolkaMarketplace = 'software' | 'plugin' | 'iot' | 'wagi' | 'panele';

/**
 * Polki sprzetowe (2026-08-18). Jedna „Urzadzenia" rozbita na trzy, bo
 * dwadziescia siedem ofert w jednym rzedzie nie jest lista, tylko sterta:
 * czujnik Shelly, waga pomostowa i panel scienny odpowiadaja na trzy rozne
 * pytania i kupuje je trzech roznych ludzi.
 *
 * `energy` idzie do IoT, a nie na wlasna polke: licznik energii Shelly jest
 * urzadzeniem IoT tak samo jak czujnik temperatury — rozni sie tym, CO mierzy,
 * a to jest kategoria dziedzinowa, nie polka.
 */
export const POLKI_URZADZEN = ['iot', 'wagi', 'panele'] as const;

export type PolkaUrzadzen = (typeof POLKI_URZADZEN)[number];

export function czyPolkaUrzadzen(polka: PolkaMarketplace): polka is PolkaUrzadzen {
  return (POLKI_URZADZEN as readonly string[]).includes(polka);
}

/**
 * Kategoria oferty na polke. Nieznana kategoria ląduje w IoT — to najszersza
 * z trzech i jedyna, w ktorej „urzadzenie, ktoremu nikt nie nadal kategorii"
 * nie klamie o swoim przeznaczeniu.
 */
export function polkaUrzadzenia(kategoria: string | null | undefined): PolkaUrzadzen {
  if (kategoria === 'weighing') return 'wagi';
  if (kategoria === 'panel') return 'panele';
  return 'iot';
}

/** Wiersz `module_categories` z zasiegiem polek (487). */
export interface KategoriaMarketplace {
  id: string;
  name_pl: string;
  icon?: string | null;
  sort_order?: number | null;
  dla_oprogramowania?: boolean | null;
  dla_wtyczek?: boolean | null;
  dla_urzadzen?: boolean | null;
}

const MONTAZ_PO_POLSKU: Record<string, string> = {
  freestanding: 'wolnostojąca',
  built_in: 'wbudowana w posadzkę',
};

/**
 * Zakres w jednostce czytelnej dla czlowieka.
 *
 * WASKIE PRZELICZENIE, NIE DRUGI KONWERTER. Zamienia WYLACZNIE gramy na
 * kilogramy i wylacznie przy pelnych tysiacach — bo katalog trzyma wagi
 * w gramach (`391`/`387`), a „do 500000 g" na metce sklepu jest nieczytelne.
 * Ogolny przelicznik jednostek mieszka w bazie (`uom_convert_delta`) i to on
 * orzeka; tutaj chodzi wylacznie o NAPIS. Cokolwiek innego niz gramy zostaje
 * pokazane tak, jak stoi w rejestrze — zgadywanie skali obcej jednostki byloby
 * dokladnie tym cichym falszem, przed ktorym broni sie caly ten katalog.
 */
export function opisZakresu(
  rangeMax: number | string | null | undefined,
  uom: string | null | undefined
): string | null {
  if (rangeMax === null || rangeMax === undefined || !uom) return null;
  const wartosc = Number(rangeMax);
  if (!Number.isFinite(wartosc) || wartosc <= 0) return null;

  if (uom === 'g' && wartosc >= 1000 && wartosc % 1000 === 0) {
    return `do ${wartosc / 1000} kg`;
  }
  return `do ${wartosc} ${uom}`;
}

/**
 * Jednolinijkowy opis metrologii — CZYTANY Z MODELU, nigdy z oferty.
 *
 * Oferta nie ma wlasnych kolumn na dzialke ani zakres i mieć ich nie bedzie:
 * gdyby je miala, sklep staby sie drugim zrodlem prawdy o tej samej wadze,
 * a rozjazd z rejestrem zobaczylby dopiero straznik dzialki — po fakcie.
 */
export function opisMetrologii(model: ModelPrzyrzaduOferty | null | undefined): string | null {
  if (!model) return null;

  const czesci: string[] = [];

  if (model.resolution !== null && model.resolution !== undefined && model.measures_uom_code) {
    czesci.push(`działka ${model.resolution} ${model.measures_uom_code}`);
  }

  const zakres = opisZakresu(model.range_max, model.measures_uom_code);
  if (zakres) czesci.push(zakres);

  // Rozmiar i montaz MUSZA tu byc: cztery warianty `W-500-100g` niosa jeden kod
  // i bez nich sa na liscie nierozroznialne (patrz 387).
  if (model.size_label) czesci.push(model.size_label);
  const montaz = model.mounting ? MONTAZ_PO_POLSKU[model.mounting] : null;
  if (montaz) czesci.push(montaz);

  return czesci.length > 0 ? czesci.join(' · ') : null;
}

export interface EtykietaCeny {
  tekst: string;
  /** Kwota brutto, gdy pozycja niesie stawke VAT. Bez stawki — `null`. */
  brutto: string | null;
  /** Czy to jest zaproszenie do rozmowy, a nie kwota. Ekran maluje to inaczej. */
  naZapytanie: boolean;
}

/**
 * Brutto z netto i stawki VAT — zaokraglone do groszy.
 *
 * DLACZEGO W OGOLE: cennik kanoniczny jest NETTO, a wlasciciel podaje ceny
 * czesci pozycji BRUTTO (czujnik: 250 zl). Bez tej funkcji jedna z tych dwoch
 * liczb musialaby byc przeliczana w glowie przy kazdej rozmowie z klientem.
 *
 * Stawka pochodzi Z WIERSZA (`vat_rate`), a nie ze stalej w kodzie: 23 % jest
 * dzis prawda dla calego katalogu, ale stala w ekranie znaczylaby, ze pierwsza
 * pozycja o innej stawce klamie po cichu, zamiast wymusic zmiane danych.
 */
export function bruttoZNetto(
  netto: number | string | null | undefined,
  vat: number | string | null | undefined
): number | null {
  if (netto === null || netto === undefined || vat === null || vat === undefined) return null;

  const kwota = Number(netto);
  const stawka = Number(vat);
  if (!Number.isFinite(kwota) || !Number.isFinite(stawka)) return null;
  if (kwota <= 0 || stawka < 0) return null;

  // Grosze całkowite, ta sama funkcja co cena w Stripe (naprawa kasy
  // 02.10.2026) — `kwota × 1,23` w zmiennym przecinku rozjeżdżało się
  // z Checkoutem o grosz przy końcówce ,50.
  return bruttoWGroszach(kwota, stawka) / 100;
}

function kwotaPoPolsku(kwota: number): string {
  return kwota.toLocaleString('pl-PL', { maximumFractionDigits: 2 });
}

/**
 * Metka cenowa.
 *
 * „Na zapytanie" NIE JEST brakiem danych do ukrycia — to prawdziwy stan
 * handlowy i ekran ma go nazwac wprost. Wymyslona kwota na metce jest
 * kosztowniejsza niz jej brak, bo brak da sie uzupelnic, a falszywa cene
 * ktos zdazy zacytowac klientowi.
 *
 * Netto zostaje kwota GLOWNA, bo taka jest podstawa faktury i taki jest cennik
 * kanoniczny; brutto idzie obok, bo o nie pyta klient.
 */
export function etykietaCeny(d: UrzadzenieOferty): EtykietaCeny {
  if (d.price_kind === 'on_request' || d.price_net === null || d.price_net === undefined) {
    return { tekst: 'Cena na zapytanie', brutto: null, naZapytanie: true };
  }

  const kwota = Number(d.price_net);
  if (!Number.isFinite(kwota)) {
    return { tekst: 'Cena na zapytanie', brutto: null, naZapytanie: true };
  }

  const waluta = d.currency ?? '';
  const sufiks = d.price_kind === 'lease_monthly' ? ' / mies.' : '';
  const brutto = bruttoZNetto(d.price_net, d.vat_rate);

  return {
    tekst: `${kwotaPoPolsku(kwota)} ${waluta} netto${sufiks}`.trim(),
    brutto: brutto === null ? null : `${kwotaPoPolsku(brutto)} ${waluta} brutto${sufiks}`.trim(),
    naZapytanie: false,
  };
}

/**
 * Zdjecia oferty — wylacznie adresy `https`.
 *
 * `photos` to JSONB, wiec z bazy moze przyjsc cokolwiek. Adres inny niz `https`
 * nie trafia do `src`: mieszana zawartosc i tak zostalaby zablokowana przez
 * przegladarke, a `javascript:` w atrybucie obrazka to nie jest rzecz, ktora
 * ma zalezec od tego, kto ostatnio edytowal katalog.
 */
export function zdjeciaOferty(d: UrzadzenieOferty): string[] {
  if (!Array.isArray(d.photos)) return [];
  return d.photos.filter((p): p is string => typeof p === 'string' && p.startsWith('https://'));
}

/**
 * Kategorie widoczne nad dana polka (487).
 *
 * Zasieg czytamy Z BAZY, bo tam jest egzekwowany kluczem obcym — powtorzona
 * tu lista „ktora kategoria jest sprzetowa" bylaby druga prawda i rozjechalaby
 * sie przy pierwszej nowej kategorii.
 *
 * Wiersz BEZ zadnej z trzech flag (stara baza, ekran wdrozony przed migracja)
 * traktujemy jako oprogramowanie — tak wygladal caly slownik przed `487`, wiec
 * to jest zachowanie sprzed zmiany, a nie zgadywanie.
 */
export function kategoriePolki(
  kategorie: KategoriaMarketplace[],
  polka: PolkaMarketplace
): KategoriaMarketplace[] {
  return kategorie.filter((k) => {
    const brakFlag =
      k.dla_oprogramowania === undefined &&
      k.dla_wtyczek === undefined &&
      k.dla_urzadzen === undefined;
    if (brakFlag) return polka === 'software';

    if (czyPolkaUrzadzen(polka)) return k.dla_urzadzen === true;
    if (polka === 'plugin') return k.dla_wtyczek === true;
    return k.dla_oprogramowania === true;
  });
}

/**
 * Na ktorej polce stoi wiersz `modules`.
 *
 * Brak `module_kind` znaczy `organ` — tak stanowi `450` i tak czyta to reszta
 * powloki (`moduleMaWlasnyEkran`).
 */
export function polkaModulu(m: { module_kind?: string | null }): 'software' | 'plugin' {
  return m.module_kind === 'integration' ? 'plugin' : 'software';
}

/**
 * Filtr listy — po nazwie, opisie ORAZ kodzie modelu z rejestru.
 *
 * Kod jest w wyszukiwaniu celowo: operator ma na wadze naklejke `W-100-60-20g`
 * i tego wlasnie szuka. Kod czytamy przez zlaczenie, wiec wyszukiwarka i karta
 * mowia o tym samym wierszu rejestru.
 */
export function filtrujUrzadzenia(
  urzadzenia: UrzadzenieOferty[],
  search: string,
  kategoria: string
): UrzadzenieOferty[] {
  const q = search.trim().toLowerCase();

  return urzadzenia.filter((d) => {
    if (kategoria !== 'all' && d.category !== kategoria) return false;
    if (!q) return true;

    const kod = d.fabrica_instrument_models?.code?.toLowerCase() ?? '';
    return (
      d.name.toLowerCase().includes(q) ||
      (d.short_description ?? '').toLowerCase().includes(q) ||
      kod.includes(q)
    );
  });
}

/**
 * Co ekran ma zrobic pod cena oferty.
 *
 * TRZY STANY, NIE DWA. Do migracji `659` byl jeden przycisk — `mailto:`
 * z tematem „Zapytanie o wycene" — i to bylo uczciwe, bo sciezki platnosci nie
 * bylo wcale. Teraz jest, ale nie dla kazdej pozycji naraz, a roznica miedzy
 * „nie mamy ceny" a „mamy cene, ktorej jeszcze nie wystawilismy" jest dla
 * kupujacego roznica miedzy „zapytaj" a „wroc jutro".
 *
 * Zlanie ich w jedno byloby ta sama usterka, co `?? 0` przy nowym polu pulsu:
 * dwa rozne stany pod jedna nazwa, z ktorych jeden pokazuje zielen tam, gdzie
 * nie ma wiedzy.
 */
export type StanZakupu = 'kup' | 'na_zapytanie' | 'w_przygotowaniu';

export function stanZakupu(d: UrzadzenieOferty): StanZakupu {
  // Kolejnosc jest istotna: „na zapytanie" to stan HANDLOWY i wygrywa
  // z technicznym. Pozycja bez ceny nie stanie sie kupowalna przez to, ze ktos
  // zapali jej flage w Stripe.
  if (d.price_kind === 'on_request' || d.price_net === null || d.price_net === undefined) {
    return 'na_zapytanie';
  }
  return d.stripe_price_active === true ? 'kup' : 'w_przygotowaniu';
}

/**
 * Kwota, ktora naprawde obciazy karte: brutto sztuki × ilosc.
 *
 * Mnozymy PO zaokragleniu sztuki, bo dokladnie tak liczy Stripe
 * (`unit_amount × quantity`) i tak liczy nasz backend (`koszyk.ts`). Policzenie
 * tego z netto calej pozycji dawaloby ekran obiecujacy inna kwote niz kasa.
 */
export function razemBrutto(d: UrzadzenieOferty, ilosc: number): number | null {
  const brutto = bruttoZNetto(d.price_net, d.vat_rate);
  if (brutto === null) return null;
  const sztuk = Number(ilosc);
  if (!Number.isInteger(sztuk) || sztuk < 1) return null;
  return Math.round(brutto * 100 * sztuk) / 100;
}
